/**
 * Authentication Context
 * Centralized auth state management
 * Integrates with /api/user/me endpoint
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { apiClient } from '@/shared/services/api';
import { API_ROUTES } from '@/shared/services/apiRoutes';
import { API_FEATURES } from '@/shared/services/config';
import { setSentryUser } from '@/lib/sentry';
import { SessionExpiredScreen } from '@/components/SessionExpiredScreen';
import { withAuthTimeout } from '@/shared/utils/withTimeout';
import { getSupabaseSession, hasSession } from '@/shared/utils/supabaseStorage';
import { pushSessionToChromeExtension } from '@/lib/chromeExtensionSession';

// Types matching mobile's authSlice
export interface User {
  id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  phone?: string | null;
  company_name?: string | null;
  company_id?: string | null;
  avatar_url?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
}

export interface Subscription {
  id: string;
  user_id: string;
  status: 'active' | 'inactive' | 'trial' | 'paused';
  plan_type: 'free' | 'basic' | 'premium' | 'enterprise';
  is_trial_active: boolean;
  trial_end_date?: string;
  stripe_customer_id?: string;
  is_paused?: boolean;
}

export interface Company {
  id: string;
  company_name: string;
  admin_user_id: string;
  created_at: string;
}

export interface UserRole {
  company_id: string;
  role: string;
}

interface AuthContextType {
  // State
  user: User | null;
  subscription: Subscription | null;
  company: Company | null;
  userRole: UserRole | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  userDataLoaded: boolean;
  hasCompletedOnboarding: boolean;
  error: string | null;
  sessionExpired: boolean;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, metadata: any) => Promise<void>;
  logout: () => Promise<void>;
  loadUserData: (userId: string) => Promise<void>;
  refreshUserData: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  /**
   * Load user data from backend API
   * SECURITY: No direct Supabase database queries - all data comes from backend
   */
  const loadUserData = useCallback(async (userId: string, retryCount = 0) => {
    try {
      console.log('[AuthContext] Loading user data for:', userId);
      console.log('[AuthContext] Using backend API (secure)');

      const response = await apiClient.get<{
        ok: boolean;
        user: any;
        subscription: any;
        company: any;
        userRole: any;
      }>(API_ROUTES.USER_ME);

      console.log('[AuthContext] RAW backend response:', response);

      if (!response.ok) {
        throw new Error('Failed to load user data');
      }

      // Map backend UserProfile (user_id) to frontend User (id)
      const mappedUser = response.user ? {
        ...response.user,
        id: response.user.user_id, // Map user_id to id
        name: response.user.full_name || response.user.name,
      } : null;

      console.log('[AuthContext] Mapped user data:', {
        user: mappedUser,
        hasCompanyId: !!mappedUser?.company_id,
        companyIdValue: mappedUser?.company_id,
        subscription: response.subscription,
        company: response.company,
        userRole: response.userRole,
      });

      // Update state
      setUser(mappedUser);
      setSubscription(response.subscription);
      setCompany(response.company);
      setUserRole(response.userRole);

      // Set Sentry user context for error tracking
      if (mappedUser) {
        setSentryUser({
          id: mappedUser.id,
          email: mappedUser.email,
          name: mappedUser.name || undefined,
        });
      }

      // Determine onboarding status
      const hasOnboarded = Boolean(
        (response.company || response.userRole) &&
        (response.user?.name || response.user?.email)
      );
      setHasCompletedOnboarding(hasOnboarded);

      setUserDataLoaded(true);
      console.log('[AuthContext] User data loaded successfully');

    } catch (err: any) {
      console.error('[AuthContext] Error loading user data:', err);

      // Retry logic for new user profile creation (404 might be due to replication delay)
      if (err.statusCode === 404 && retryCount < 3) {
        console.log(`[AuthContext] User profile not found, retrying in ${(retryCount + 1) * 1000}ms... (attempt ${retryCount + 1}/3)`);
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 1000));
        return loadUserData(userId, retryCount + 1);
      }

      // Check if this is an auth error (expired token, invalid session, etc.) OR user deleted
      if (err.message?.includes('JWT') ||
          err.message?.includes('expired') ||
          err.message?.includes('invalid') ||
          err.message?.includes('SESSION_EXPIRED') ||
          err.statusCode === 401 ||
          (err.statusCode === 404 && retryCount >= 3)) { // User profile doesn't exist after retries (user was deleted)
        console.log('[AuthContext] Session expired or user deleted, clearing auth state and signing out');
        setSessionExpired(true);
        setIsAuthenticated(false);
        setSession(null);
        setUser(null);

        // Use Supabase Auth SDK for logout (not a database query)
        await supabase.auth.signOut();

        // Force clear localStorage to prevent cache issues
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('sb-localhost-auth-token');
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-') || key.includes('supabase')) {
            localStorage.removeItem(key);
          }
        });

        // Force redirect to landing page to bypass any stuck state
        console.log('[AuthContext] Forcing redirect to landing page');
        window.location.href = '/';
        return; // Stop execution after redirect
      } else {
        setError(err.message);
      }

      setUserDataLoaded(true); // Mark as loaded even on error
    }
  }, []);

  /**
   * Login with email/password
   * Uses Supabase Auth SDK for authentication (not database queries)
   */
  const login = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Supabase Auth SDK is OK to use (not a database query)
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (loginError) throw loginError;

      if (data.user) {
        setSession(data.session);
        setIsAuthenticated(true);
        // Load user data from backend API (not Supabase DB)
        await loadUserData(data.user.id);
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadUserData]);

  /**
   * Signup with email/password
   * Uses Supabase Auth SDK for authentication (not database queries)
   * SECURITY: Uses backend API to initialize company (no direct Supabase RPC)
   */
  const signup = useCallback(async (email: string, password: string, metadata: any) => {
    try {
      setIsLoading(true);
      setError(null);

      // Supabase Auth SDK is OK to use (not a database query)
      const { data, error: signupError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      if (signupError) throw signupError;

      if (data.user) {
        setSession(data.session);
        setIsAuthenticated(true);

        // Initialize company through backend API (replaces Supabase RPC)
        try {
          await apiClient.post(API_ROUTES.USER_INITIALIZE_COMPANY || '/user/initialize-company');
        } catch (initError) {
          console.error('[AuthContext] Failed to initialize company:', initError);
          // Continue anyway - company can be initialized during onboarding
        }

        // Load user data from backend API (not Supabase DB)
        await loadUserData(data.user.id);
      }
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadUserData]);

  /**
   * Logout
   * Uses Supabase Auth SDK for sign out (not database queries)
   */
  const logout = useCallback(async () => {
    try {
      // Supabase Auth SDK is OK to use (not a database query)
      await supabase.auth.signOut();

      // Clear all state
      setUser(null);
      setSubscription(null);
      setCompany(null);
      setUserRole(null);
      setSession(null);
      setIsAuthenticated(false);
      setUserDataLoaded(false);
      setHasCompletedOnboarding(false);
      setError(null);

      // Clear Sentry user context
      setSentryUser(null);
      pushSessionToChromeExtension(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Refresh user data without requiring userId
   * Useful for refreshing after profile updates
   */
  const refreshUserData = useCallback(async () => {
    try {
      if (!session?.user?.id) {
        console.warn('[AuthContext] Cannot refresh user data - no active session');
        return;
      }

      console.log('[AuthContext] Refreshing user data...');
      await loadUserData(session.user.id);
    } catch (err: any) {
      console.error('[AuthContext] Error refreshing user data:', err);
    }
  }, [session, loadUserData]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check session on mount and auth state changes
   * CRITICAL FIX: Uses localStorage + backend validation instead of getSession()
   * This prevents hanging when tab is inactive
   * OPTIMIZATION: Immediately marks as loaded for public users (no session)
   */
  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      try {
        console.log('[AuthContext] Checking session...');

        // STEP 1: Check localStorage for token (synchronous, no hanging)
        const storedSession = getSupabaseSession();

        if (!mounted) return;

        if (!storedSession || !storedSession.access_token || !storedSession.user) {
          console.log('[AuthContext] No stored session found');
          // ✅ OPTIMIZATION: Immediately set all states for public/guest users
          // This prevents any flash of loading screens or error modals
          setSession(null);
          setIsAuthenticated(false);
          setUserDataLoaded(true);
          setIsLoading(false);
          setUser(null);
          setSubscription(null);
          setCompany(null);
          setUserRole(null);
          pushSessionToChromeExtension(null);
          return;
        }

        console.log('[AuthContext] Found stored session, loading user data...');

        // Create session object from localStorage data
        const session: Session = {
          access_token: storedSession.access_token,
          refresh_token: storedSession.refresh_token,
          expires_at: storedSession.expires_at,
          expires_in: storedSession.expires_in,
          token_type: storedSession.token_type || 'bearer',
          user: storedSession.user,
        };

        setSession(session);
        setIsAuthenticated(true);
        pushSessionToChromeExtension(session);

        // Use existing loadUserData function (validates token with backend)
        await loadUserData(storedSession.user.id);

      } catch (err: any) {
        console.error('[AuthContext] Session check error:', err);

        // Check if this is an auth error
        if (err.message?.includes('JWT') ||
            err.message?.includes('expired') ||
            err.message?.includes('invalid') ||
            err.statusCode === 401) {
          setSessionExpired(true);
        }

        setIsAuthenticated(false);
        setUserDataLoaded(true);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    checkSession();

    // Listen for auth changes (using Supabase Auth SDK - not database queries)
    // This is safe to use as it only monitors auth state, not database data
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthContext] Auth state change:', event);

        if (!mounted) return;

        if (event === 'SIGNED_IN' && session) {
          setSession(session);
          setIsAuthenticated(true);
          setSessionExpired(false); // Reset session expired flag on new sign in
          pushSessionToChromeExtension(session);
          // Load user data from backend API (not Supabase DB)
          await loadUserData(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          // Check if this was a user-initiated logout or session expiry
          // If user data was loaded previously, this is likely a session expiry
          const wasAuthenticated = userDataLoaded;

          setUser(null);
          setSubscription(null);
          setCompany(null);
          setUserRole(null);
          setSession(null);
          setIsAuthenticated(false);
          setUserDataLoaded(true);
          setHasCompletedOnboarding(false);
          pushSessionToChromeExtension(null);

          // Show session expired screen if user was previously authenticated
          if (wasAuthenticated) {
            console.log('[AuthContext] Session expired - showing session expired screen');
            setSessionExpired(true);
          }
        } else if (event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] Token refreshed successfully');
          if (session) {
            setSession(session);
            pushSessionToChromeExtension(session);
          }
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [loadUserData]);

  const value: AuthContextType = {
    user,
    subscription,
    company,
    userRole,
    session,
    isAuthenticated,
    isLoading,
    userDataLoaded,
    hasCompletedOnboarding,
    error,
    sessionExpired,
    login,
    signup,
    logout,
    loadUserData,
    refreshUserData,
    clearError,
  };

  // Show session expired screen (uses React Router navigate, NOT window.location)
  if (sessionExpired && !isLoading) {
    return <SessionExpiredScreen />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
